import { useMemo } from "react"
import { first, isNumber } from "lodash"
import { VALUE_FORMATS } from "helpers/ai"
import { formatValueForDisplay, getColumnType, parseValue } from "connected-components/ai-teammate/helpers/formatting"
import { isPivotable } from "connected-components/ai-teammate/helpers"
import type { ArtifactColumn } from "connected-components/dashboard/modules/ai-artifact/types"
import type { GridColDef } from "@mui/x-data-grid-premium"

export interface UseTableArtifactDataReturn {
  columns: GridColDef[]
  rows: Record<string, unknown>[]
  columnHeaders: string[]
  columnFormats: Record<string, string>
}

export interface TableArtifactDataInput {
  columns: ArtifactColumn[]
  records: Record<string, unknown>[]
}

function toCanonicalKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "")
}

/**
 * Resolve a value from a data row using accessor candidates.
 * Tries exact key match first, then canonicalized match (ignores case/spaces/symbols).
 */
function getValueFromRecord(record: Record<string, unknown>, accessorCandidates: string[]) {
  const directMatchAccessor = accessorCandidates
    .filter(Boolean)
    .find(candidate => Object.hasOwn(record, candidate))

  if (directMatchAccessor) {
    return record[directMatchAccessor]
  }

  const canonicalCandidates = new Set(accessorCandidates
    .filter(Boolean)
    .map(toCanonicalKey))

  if (canonicalCandidates.size === 0) {
    return undefined
  }

  return Object.entries(record)
    .find(([recordKey]) => canonicalCandidates.has(toCanonicalKey(recordKey)))
    ?.[1]
}

/**
 * Build DataGrid-ready columns/rows from an AI table artifact payload.
 * Columns are normalized to col_0, col_1, etc.; rows are mapped using metadata accessors.
 */
export function useTableArtifactData(artifact: TableArtifactDataInput, currency?: string): UseTableArtifactDataReturn {
  const { columns: rawColumns, records } = artifact

  const hasRecords = records?.length > 0
  const hasColumnMetadata = rawColumns?.length > 0
  const firstRecord = first(records)

  const columnAccessors = useMemo(() => {
    if (!hasColumnMetadata) {
      return []
    }
    return rawColumns.map((column) => {
      return column.id || column.field || column.key || column.name || column.label
    })
  }, [hasColumnMetadata, rawColumns])

  const columnHeaders = useMemo(() => {
    if (hasColumnMetadata) {
      return rawColumns.map((col) => col.label)
    }
    return firstRecord ? Object.keys(firstRecord) : []
  }, [rawColumns, firstRecord, hasColumnMetadata])

  const columnFormats = useMemo(() => {
    return columnHeaders.reduce<Record<string, string>>((formats, key, index) => {
      const fieldName = `col_${index}`
      if (hasColumnMetadata) {
        const columnMetadata = rawColumns[index]
        formats[fieldName] = columnMetadata.format
      } else {
        formats[fieldName] = isNumber(firstRecord?.[key]) ? VALUE_FORMATS.NUMBER : VALUE_FORMATS.STRING
      }
      return formats
    }, {})
  }, [columnHeaders, hasColumnMetadata, rawColumns, firstRecord])

  const columns = useMemo(() => {
    return columnHeaders.map((headerName, index) => {
      const fieldName = `col_${index}`
      const format = columnFormats[fieldName]

      return {
        field: fieldName,
        headerName,
        type: getColumnType(format),
        minWidth: 50,
        valueGetter: (value: unknown) => parseValue(value, format),
        valueFormatter: (value: unknown) => formatValueForDisplay(value, format, currency),
        hideable: true,
        pivotable: isPivotable(headerName, records),
        disableReorder: false,
      } satisfies GridColDef
    })
  }, [columnHeaders, records, columnFormats, currency])

  const rows = useMemo(() => {
    if (!hasRecords) return []

    return records.map((record, rowIndex) => {
      return columnHeaders.reduce<Record<string, unknown>>(
        (mappedRecord, columnHeader, colIndex) => {
          const fieldName = `col_${colIndex}`
          const accessorCandidates = hasColumnMetadata
            ? [columnAccessors[colIndex], columnHeader]
            : [columnHeader]
          mappedRecord[fieldName] = getValueFromRecord(record, accessorCandidates)
          return mappedRecord
        },
        { id: rowIndex },
      )
    })
  }, [hasRecords, records, columnHeaders, hasColumnMetadata, columnAccessors])

  return {
    columns,
    rows,
    columnHeaders,
    columnFormats,
  }
}
